import { DatePicker, RangePickerProps } from '@arco-design/web-react';
import { DefaultOptionType } from '@bugly/common';
import { useControllableValue, useInterval } from 'ahooks';
import {  DatePicker as AntdDatePicker, Select, Space, DatePickerProps, SelectProps, Tag } from 'antd';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { Moment } from 'moment';
import moment from 'moment/moment';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import i18n from '@/i18n';
import { CommonDateRange } from '@/infrastructure/utils/timestamp';
import { BaseFormItemProps, PropsWithStyles } from '@/presentation/components/tds-form/type';
import './index.module.scss';
import { TimeType } from '@/proto/metric';
import { DEFAULT_EVENT_TIME_RANGE } from '../config';
import { TdsInput } from '../tds-input';
import { CUSTOM, shortRanges } from '../utils';

const { RangePicker } = DatePicker;

export type TdsDateCascaderValue = {
  time_window_type?: TimeType;
  date_value?: [Moment, Moment];
  date_type?: string;
  short_cut?: string;
};

const DEFAULT_FORMATTER = 'MM/DD HH:mm';
const DATE_PICKER_FORMATTER = 'YYYY/MM/DD';

const timeQuickSetting: Record<string, any> = {
  [TimeType.MINUTE5]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近1小时'),
      value: '近1小时',
    },
    {
      label: i18n.t('今天'),
      value: '今天',
    },
    {
      label: i18n.t('昨天'),
      value: '昨天',
    },
  ],
  [TimeType.HOUR]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近1小时'),
      value: '近1小时',
    },
    {
      label: i18n.t('今天'),
      value: '今天',
    },
    {
      label: i18n.t('昨天'),
      value: '昨天',
    },
  ],
  [TimeType.DAY]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.WEEK]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.MONTH]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.MINUTE]: [],
  [TimeType.NULL]: [],
};

export enum DatePickerType {
  DatePicker = 'DatePicker',
  RangePicker = 'RangePicker'
}

export interface TdsDateCascaderProps extends BaseFormItemProps<any> {
  timeWindowOptions?: DefaultOptionType[];
  shortCutOptions?: DefaultOptionType[];
  timeTypeOptions?: DefaultOptionType[];
  rangePickerProps?: RangePickerProps;
  limitedDays?: Record<any, number>;
  maxLimitedDays: number;
  showTimeType?: boolean;
  showTimeWindow?: boolean;
  showTimeQuick?: boolean;
  formatter?: string;
  datePickerType?: DatePickerType;
  datePickerProps?: (value: TdsDateCascaderValue) => DatePickerProps;
  customInitTimeRange?: (timeWindow: TimeType, shortCut: string) => Moment[]; // 自定义获取时间规则
  customTimeQuickSetting?: (timeType: TimeType) => SelectProps['options'] // 自定义快捷选项
  customShortCuts?: any; // 自定义快捷选项，不区分timeWindow
  customShortRanges?: Record<string, Moment[]>; // 自定义快捷选项时间范围
  limitRange?: number;// 限制所能选择的时间范围，和limitedDays不同，列如limitRange为7天，limitedDays为90天，则表现为在90天内可选择任意7天的时间范围
};

export const getInitTimeRange = (timeWindow: TimeType, shortCut: string) => {
  if (shortCut === CUSTOM) {
    switch (timeWindow) {
      case TimeType.MONTH:
        return CommonDateRange.lastDaysFull(90);
      case TimeType.WEEK:
        return CommonDateRange.lastDaysFull(30);
      case TimeType.DAY:
        return CommonDateRange.lastDaysFull(7);
      default:
        return CommonDateRange.today();
    }
  }
  if (shortCut === undefined ||  shortRanges[shortCut] === undefined) {
    return [];
  }

  return shortRanges[shortCut]();
};

export const TdsDateCascader = (props: PropsWithStyles<TdsDateCascaderProps>) => {
  const {
    timeWindowOptions,
    showTimeWindow = true,
    showTimeType = true,
    formatter = DEFAULT_FORMATTER,
    datePickerType = DatePickerType.RangePicker,
    datePickerProps,
    customInitTimeRange,
    customTimeQuickSetting,
    customShortCuts,
    limitRange,
  } = props;
  const [value, setValue] = useControllableValue<TdsDateCascaderValue>(props, {
    defaultValue: {
      date_type: undefined,
      date_value: undefined,
      time_window_type: undefined,
      short_cut: undefined,
    },
  });
  const [interval, setInterval] = useState<number>();
  const [dynamicDate, setDynamicDate] = useState<Moment[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);


  // 当值为空时，自动设置默认值
  useEffect(() => {
    // 如果已经初始化过且有值，不再处理
    if (isInitialized) {
      return;
    }

    // 判断当前值是否为空：所有关键字段都未定义或为空
    const hasDateValue = value?.date_value
      && Array.isArray(value.date_value)
      && value.date_value.length === 2
      && value.date_value[0]
      && value.date_value[1];

    const isValueEmpty = !value || (
      !hasDateValue
      && !value?.time_window_type
      && !value?.short_cut
      && !value?.date_type
    );

    if (isValueEmpty) {
      // 设置默认值（使用新的 Moment 实例，确保时间是最新的）
      const timeWindowType = props.timeWindowOptions?.[0]?.value as TimeType || TimeType.MINUTE5;
      const dateType = props.timeTypeOptions?.[0]?.value as string || 'event_time';
      setValue({
        ...DEFAULT_EVENT_TIME_RANGE,
        date_type: dateType,
        time_window_type: timeWindowType,
        short_cut: CUSTOM,
        date_value: customInitTimeRange
          ? customInitTimeRange(timeWindowType, CUSTOM)
          : getInitTimeRange(timeWindowType, CUSTOM),
      });
    }
    setIsInitialized(true);
  }, [value, setValue]);

  const clear = useInterval(() => {
    const dateRange = customInitTimeRange
      ? customInitTimeRange(value.time_window_type as TimeType, value.short_cut ?? '') : getInitTimeRange(value.time_window_type as TimeType, value.short_cut ?? '');
    setDynamicDate(dateRange);
  }, interval);

  const [pickerVisible, setPickerVisible] = useState(false);

  const timeQuickRef = useRef<any>(value.short_cut);

  const onDateChange = (_dateString: any, date: any) => {
    const [start, end] = date || [];
    setValue({
      ...value,
      date_value: [start?.format('YYYY-MM-DD HH:mm:00'), end?.format('YYYY-MM-DD HH:mm:59')],
      short_cut: CUSTOM,
    });
  };

  const quickSetting: any = customTimeQuickSetting
    ? customTimeQuickSetting(value.time_window_type || TimeType.MINUTE5)
    : timeQuickSetting[value.time_window_type || TimeType.MINUTE5] || [];
  const defaultShortCuts = customShortCuts ? customShortCuts
    : (quickSetting).filter((item: any) => item.value !== 'custom')
      .map((item: any) => ({
        text: item.label,
        shortCut: item.value,
        value: shortRanges?.[item.value],
      }));

  const extraFooter = (
    <div style={{ padding: '8px', borderBottom: '1px solid rgba(229, 230, 235)' }}>
      <Space>
        {defaultShortCuts?.map((item: any) => <Tag
            color="processing"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onShortCutChange(item.shortCut ?? item.text);
              setPickerVisible(false);
            }}
          >{item.text}</Tag>)}
      </Space>
    </div>
  );

  const panelRender = (panelNode: ReactNode) => {
    const extra = !isEmpty(defaultShortCuts) ? extraFooter : null;
    return (
      <div>
        {extra && <div style={{ padding: 8, borderBottom: '1px solid #e5e6eb' }}>
        {extra}
        </div>}
        {panelNode}
      </div>
    );
  };

  const renderShortCut = () => {
    const dynamicText = !isEmpty(dynamicDate)
      ? `（${dynamicDate?.[0]?.format(formatter)} ~ ${dynamicDate?.[1]?.format(formatter)}）`
      : '';
    return `${value.short_cut ? i18n.t(value.short_cut) : ''}${dynamicText}`;
  };

  const renderDatePicker = () => {
    if (datePickerType === DatePickerType.DatePicker) {
      return <AntdDatePicker
        style={{ flex: 1 }}
        onChange={(date) => {
          onDateChange(undefined, date ? [moment(date).startOf('date'),
            moment(date).endOf('date')] : undefined);
        }}
        value={value?.date_value?.[0]}
        format={DATE_PICKER_FORMATTER}
        disabledDate={disabledDate}
        disabled={value.short_cut !== CUSTOM}
        allowClear={false}
        {...datePickerProps?.(value)}
      />;
    }

    return (
      <div  className='flex flex-col h-[32px] flex-1'>
        <TdsInput
          className='tds-range-picker-repeat'
          style={{ position: 'relative', zIndex: 2 }}
          value={renderShortCut()}
          onFocus={() => setPickerVisible(true)}
          hidden={value.short_cut === CUSTOM} />
        <RangePicker
          className={'tds-range-picker'}
          onChange={onDateChange}
          value={value.date_value as any}
          format={formatter}
          disabledDate={disabledDate}
          // @ts-ignore
          showTime
          // shortcuts={defaultShortCuts}
          allowClear={false}
          {...props?.rangePickerProps}
          onSelectShortcut={onSelectShortcut}
          onVisibleChange={visible => setPickerVisible(visible as boolean)}
          popupVisible={pickerVisible}
          style={{ flex: 1 }}
          panelRender={panelRender}
          onSelect={(_dateString, values, extra) => {
            if (limitRange === null || limitRange === undefined) {
              return;
            }
            if (extra.type === 'start') {
              if (values?.[0].diff(values?.[1], 'days') <= -limitRange || !values?.[1]) {
                setValue(value => ({
                  ...value,
                  date_value: [values?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
                    getDisabledDate(values?.[0].add(limitRange, 'days'))?.format('YYYY-MM-DD HH:mm:ss')] as any,
                  short_cut: CUSTOM,
                }));
              }
            }
            if (extra.type === 'end') {
              if (values?.[1].diff(values?.[0], 'days') >= limitRange || !values?.[0]) {
                setValue(value => ({
                  ...value,
                  date_value: [getDisabledDate(values?.[1].subtract(limitRange, 'days'))?.format('YYYY-MM-DD HH:mm:ss'),
                    values?.[1]?.format('YYYY-MM-DD HH:mm:ss')] as any,
                  short_cut: CUSTOM,
                }));
              }
            }
          }}
        />
      </div>
    );
  };

  const onSelectShortcut = (shortCut: any) => {
    timeQuickRef.current = shortCut.text;
    setPickerVisible(false);
  };

  const onTimeWindowChange = (val: TimeType) => {
    setValue({
      ...value,
      short_cut: CUSTOM,
      time_window_type: val,
      date_value: customInitTimeRange
        ? customInitTimeRange(val, CUSTOM)
        : getInitTimeRange(val, CUSTOM),
    });
  };

  const onShortCutChange = (val: string) => {
    const dateRange = customInitTimeRange
      ? customInitTimeRange(value.time_window_type as TimeType, val)
      : getInitTimeRange(value.time_window_type as TimeType, val);

    setValue({
      ...value,
      short_cut: val,
      date_value: dateRange,
    });

    if (val !== CUSTOM) {
      setDynamicDate(dateRange);
      setInterval(60000);
    }
  };

  const onTimeTypeChange = (val: string) => {
    setValue({
      ...value,
      date_type: val,
    });
  };

  const disabledDate = (current: any) => {
    let limitedDays = 1;

    switch (value.time_window_type) {
      case TimeType.MINUTE5:
        limitedDays = props.limitedDays?.[TimeType.MINUTE5] ?? 1;
        break;
      case TimeType.HOUR:
        limitedDays = props.limitedDays?.[TimeType.HOUR] ?? 13;
        break;
      case TimeType.DAY:
        limitedDays = props.limitedDays?.[TimeType.DAY] ?? props.maxLimitedDays;
        break;
      case TimeType.WEEK:
        limitedDays = props.limitedDays?.[TimeType.WEEK] ?? props.maxLimitedDays;
        break;
      case TimeType.MONTH:
        limitedDays = props.limitedDays?.[TimeType.MONTH] ?? props.maxLimitedDays;
        break;
      default:
        break;
    }

    const limitStart = moment().startOf('date')
      .subtract(limitedDays, 'days');

    return current.isAfter(moment()) || current.isBefore(limitStart);
  };

  const getDisabledDate = (current: any) => {
    let limitedDays = 1;

    switch (value.time_window_type) {
      case TimeType.MINUTE5:
        limitedDays = props.limitedDays?.[TimeType.MINUTE5] ?? 1;
        break;
      case TimeType.HOUR:
        limitedDays = props.limitedDays?.[TimeType.HOUR] ?? 13;
        break;
      case TimeType.DAY:
        limitedDays = props.limitedDays?.[TimeType.DAY] ?? props.maxLimitedDays;
        break;
      case TimeType.WEEK:
        limitedDays = props.limitedDays?.[TimeType.WEEK] ?? props.maxLimitedDays;
        break;
      case TimeType.MONTH:
        limitedDays = props.limitedDays?.[TimeType.MONTH] ?? props.maxLimitedDays;
        break;
      default:
        break;
    }

    const limitStart = moment().startOf('date')
      .subtract(limitedDays, 'days');
    if (current.isAfter(moment())) {
      return moment().endOf('days');
    }
    if (current.isBefore(limitStart)) {
      return limitStart;
    }
    return current;
  };

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (value.short_cut !== CUSTOM) {
      onShortCutChange(value.short_cut ?? '');
    } else {
      clear();
    }
  }, [value.short_cut, isInitialized]);
  return (
    <div className={classNames({
      flex: true,
      'gap-[8px]': true,
      'tds-form-datecascader': true,
      'tds-form-datecascader-hidden': value.short_cut !== CUSTOM,
    }, props.className)} style={props.style}>
      {showTimeWindow && <Select
        onChange={onTimeWindowChange}
        style={{ width: 110 }}
        dropdownClassName={'tds-form-select-dropdown'}
        options={timeWindowOptions}
        value={value.time_window_type}
      />}
      {/* {showTimeQuick ? (<div style={{ display: 'none' }}>
        <Select
        onChange={onShortCutChange}
        style={{ width: 110 }}
        dropdownClassName={'tds-form-select-dropdown'}
        options={customTimeQuickSetting
          ? customTimeQuickSetting(value.time_window_type || TimeType.MINUTE5)
          : timeQuickSetting[value.time_window_type || TimeType.MINUTE5]}
        value={value.short_cut}
      />
      </div>) : null} */}
      {renderDatePicker()}
      {showTimeType && <Select
        style={{ width: 102 }}
        dropdownClassName={'tds-form-select-dropdown'}
        options={props.timeTypeOptions}
        value={value.date_type}
        onChange={onTimeTypeChange}
      />}
    </div>
  );
};
